import { MoonIcon, SunIcon } from '@heroicons/react/24/solid';
import { useTheme } from '../../lib/theme';
import { IconButton } from '../ui/IconButton';

export interface ThemeToggleProps {
  /** Additional classes — story 2-132 uses this to fold the shell header's own instance onto the shared `h-control` shape without affecting the other two call sites (PublicHeader, SettingsPage's Appearance action), which keep their own untouched default sizing. */
  className?: string;
}

// Story 2-147c: migrated onto `IconButton` — this control is icon-ONLY below
// `sm` (the raw <button> the sweep counted) but keeps its visible "Light
// mode"/"Dark mode" text at `sm` and up, so it passes `children` (suppressing
// IconButton's fixed icon-only box) rather than the plain `icon` prop alone.
// The accessible name stays `aria-label="Toggle theme"` either way — unchanged
// string, unchanged mechanism.
//
// Story 2-147xd: left as a direct `IconButton` — this hybrid "icon-only
// below `sm`, icon+visible-text at `sm` and up" mode has no equivalent in
// `ActionDescriptor` (its `iconOnly` flag is a hard either/or: `IconButton`
// with `aria-label`-only naming, or `Button` with a plain string `label` —
// neither branch supports `IconButton` WITH visible `children`, the one
// mechanism this control's own responsive behaviour depends on). Also this
// component's own three call sites (shell header, `PublicHeader`, Settings'
// Appearance action) are never rendered as siblings of another action, so
// there is no cluster/overflow need a host would add value to either.
export function ThemeToggle({ className }: ThemeToggleProps = {}): JSX.Element {
  const { mode, toggle } = useTheme();

  return (
    <IconButton
      onClick={toggle}
      variant="secondary"
      className={`gap-2 text-xs font-medium sm:text-sm ${className ?? ''}`}
      aria-label="Toggle theme"
      icon={
        mode === 'dark' ? (
          <SunIcon className="h-4 w-4 text-foreground" />
        ) : (
          <MoonIcon className="h-4 w-4 text-foreground" />
        )
      }
    >
      <span className="hidden sm:inline">{mode === 'dark' ? 'Light mode' : 'Dark mode'}</span>
    </IconButton>
  );
}
